import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { MsalGuard } from "@azure/msal-angular";
import { AuthGuard } from "./_core/interceptors/auth-guard";
import { Policies } from "./_core/models/main/policies.enum";
import { unsavedChangesGuard } from "./_core/guards/unsaved-changes.guard";
import { AdminPageComponent } from "./modules/main/pages/admin/admin.page";
import { RolesPageComponent } from "./modules/main/pages/roles/roles.page";
import { PositionsPageComponent } from "./modules/main/pages/positions/positions.page";
import { AdminHomePageComponent } from "./modules/main/pages/admin-home/admin-home.page";
import { CentroPageComponent } from "./modules/main/pages/centro/centro.page";
import { HomeComponent } from "./modules/main/pages/home/home.page";
import { PowerbiComponent } from "./modules/main/pages/powerbi/powerbi.component";
import { ErrorComponent } from "./modules/shared/error/error.component";
import { ErrorLayoutComponent } from "./modules/shared/error-layout/error-layout.component";
import { LayoutComponent } from "./modules/shared/layout/layout.component";
import { NoPermissionsComponent } from "./modules/shared/no-permissions/no-permissions.component";
import { CentrosListComponent } from "./modules/main/pages/centros/centros-list.page";

const routes: Routes = [
	// Rutas de error y sin-permisos: SIN guards y FUERA del layout autenticado.
	// Si la sesión está rota, estas páginas deben poder renderizar siempre
	// (con MsalGuard delante se producía pantalla en blanco / bucles).
	// ⚠️ Rutas con path EXPLÍCITO, nunca un `path: ""` agrupador: un padre de path
	// vacío con hijos que no casan se activa igualmente en la raíz con el outlet
	// vacío → pantalla en blanco en /#/ (bug 2026-07-05).
	{
		path: "error",
		component: ErrorLayoutComponent,
		children: [{ path: "", component: ErrorComponent }],
	},
	{
		path: "no-permissions",
		component: ErrorLayoutComponent,
		children: [{ path: "", component: NoPermissionsComponent }],
	},
	{
		path: "",
		component: LayoutComponent,
		canActivate: [MsalGuard],
		children: [
			{
				path: "home",
				component: HomeComponent,
				// Sin policies: accesible a cualquier usuario autenticado con perfil.
				data: {
					breadcrumb: null,
					animation: "home",
				},
				canActivate: [MsalGuard, AuthGuard],
			},
			{
				path: "map",
				data: {
					policies: [Policies.Centro_View],
					breadcrumb: null,
				},
				canActivate: [MsalGuard, AuthGuard],
				loadChildren: () =>
					import("./modules/main/pages/map/map.module").then(
						(m) => m.MapModule,
					),
			},
			{
				path: "sites",
				component: CentrosListComponent,
				data: {
					policies: [Policies.Centro_View],
					breadcrumb: null,
				},
				canActivate: [MsalGuard, AuthGuard],
			},
			{
				path: "sites/:centroId",
				component: CentroPageComponent,
				data: {
					policies: [Policies.Centro_View],
				},
				canActivate: [MsalGuard, AuthGuard],
				canDeactivate: [unsavedChangesGuard],
			},
			{
				// Página de conceptos (onboarding visual): standalone lazy, sin policy
				// específica (visible para cualquier usuario autenticado).
				path: "concepts",
				loadComponent: () =>
					import(
						"./modules/main/pages/conceptos/conceptos.page"
					).then((m) => m.ConceptosPageComponent),
				canActivate: [MsalGuard, AuthGuard],
			},
			{
				path: "admin",
				component: AdminHomePageComponent,
				data: {
					policies: [Policies.Admin_Permissions],
				},
				canActivate: [MsalGuard, AuthGuard],
			},
			{
				path: "admin/permissions",
				component: AdminPageComponent,
				data: {
					policies: [Policies.Admin_Permissions],
				},
				canActivate: [MsalGuard, AuthGuard],
				canDeactivate: [unsavedChangesGuard],
			},
			{
				path: "admin/roles",
				component: RolesPageComponent,
				data: {
					policies: [Policies.Admin_Roles],
				},
				canActivate: [MsalGuard, AuthGuard],
			},
			{
				// Funciones y roles de persona (ADR-0005): catálogo Puesto. Los writes van
				// gateados en back por Persona.Manage; la ruta exige la misma capacidad.
				path: "admin/positions",
				component: PositionsPageComponent,
				data: {
					policies: [Policies.Persona_Manage],
				},
				canActivate: [MsalGuard, AuthGuard],
			},
			{
				path: "powerbi",
				data: {
					policies: [Policies.Admin_Permissions],
					breadcrumb: null,
				},
				canActivate: [MsalGuard, AuthGuard],
				loadChildren: () =>
					import("./modules/main/pages/powerbi/powerbi.module").then(
						(m) => m.PowerbiModule,
					),
			},
			{
				path: "my-profile",
				canActivate: [MsalGuard, AuthGuard],
				data: { breadcrumb: null },
				loadChildren: () =>
					import("./modules/main/pages/mi-perfil/mi-perfil.module").then(
						(m) => m.MiPerfilModule,
					),
			},
			{
				// Cartera del usuario responsable: sin policies (cada usuario ve solo
				// lo suyo; el filtrado real lo hace back por usuario autenticado).
				path: "my-responsibilities",
				canActivate: [MsalGuard, AuthGuard],
				data: { breadcrumb: null },
				loadChildren: () =>
					import(
						"./modules/main/pages/mis-responsabilidades/mis-responsabilidades.module"
					).then((m) => m.MisResponsabilidadesModule),
			},
			{
				// Gestión energética global (lista de trabajo de CUPS): SOLO perfiles
				// con App.Energia.View (GESTION_ENERGIA / ADMIN); el back revalida.
				path: "energy",
				canActivate: [MsalGuard, AuthGuard],
				data: { breadcrumb: null, policies: [Policies.Energia_View] },
				loadChildren: () =>
					import("./modules/main/pages/energia/energia.module").then(
						(m) => m.EnergiaModule,
					),
			},
			{
				path: "opportunities",
				canActivate: [MsalGuard, AuthGuard],
				data: {
					policies: [Policies.Oportunidad_View],
					breadcrumb: null,
				},
				loadChildren: () =>
					import("./modules/opportunities/opportunities.module").then(
						(m) => m.OpportunitiesModule,
					),
			},
			{
				path: "contracts",
				canActivate: [MsalGuard, AuthGuard],
				data: {
					policies: [Policies.Contrato_View],
					breadcrumb: null,
				},
				loadChildren: () =>
					import("./modules/contracts/contracts.module").then(
						(m) => m.ContractsModule,
					),
			},
			{
				path: "people",
				canActivate: [MsalGuard, AuthGuard],
				data: {
					policies: [Policies.Persona_View],
					breadcrumb: null,
				},
				loadChildren: () =>
					import("./modules/contacts/contacts.module").then(
						(m) => m.ContactsModule,
					),
			},
			{
				path: "profit-centers",
				canActivate: [MsalGuard, AuthGuard],
				data: {
					policies: [Policies.Cebe_View],
					breadcrumb: null,
				},
				loadChildren: () =>
					import("./modules/cebes/cebes.module").then(
						(m) => m.CebesModule,
					),
			},
			{
				path: "",
				redirectTo: "home",
				pathMatch: "full",
			},
			{
				path: "**",
				redirectTo: "home",
			},
		],
	},
];

@NgModule({
	imports: [RouterModule.forRoot(routes)],
	exports: [],
})
export class AppRoutingModule {}
